import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Upload, Sparkles, Mic, MicOff, Lock, Loader2, ImageIcon, Film, Coins, ArrowRight, Check, AlertTriangle, BookmarkPlus } from "lucide-react";
import api, { formatApiErrorDetail, BACKEND_URL } from "@/lib/api";
import { effectiveTier } from "@/lib/AuthContext";

const QUALITY_OPTIONS = [
  { id: "draft",  label: "Draft",  desc: "Fast & lean. Test ideas." },
  { id: "pro",    label: "Pro",    desc: "Balanced — recommended." },
  { id: "cinema", label: "Cinema", desc: "Highest quality (Sora 2 Pro)." },
];
const DURATION_OPTIONS = [
  { id: 4,  label: "4s · Quick" },
  { id: 8,  label: "8s · Standard" },
  { id: 12, label: "12s · Extended" },
];
const VOICES = ["onyx", "alloy", "ash", "coral", "echo", "fable", "nova", "sage", "shimmer"];

const STEPS = [
  { id: 1, label: "Media Source" },
  { id: 2, label: "Voiceover" },
  { id: 3, label: "Quality & Length" },
  { id: 4, label: "Review & Generate" },
];

/**
 * CreatorWizard — the interactive multi-step modal for spinning up a render
 * from a template. Renders nothing if `template` is null.
 */
export default function CreatorWizard({ template, user, onClose }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [mediaSource, setMediaSource] = useState("generate"); // "generate" | "upload"
  const [asset, setAsset] = useState(null); // { asset_id, preview_url, kind }
  const [uploading, setUploading] = useState(false);
  const [voTone, setVoTone] = useState(false);
  const [voice, setVoice] = useState("onyx");
  const [voScript, setVoScript] = useState("");
  const [quality, setQuality] = useState(template?.recommended_quality || "pro");
  const [duration, setDuration] = useState(template?.default_duration && template.default_duration <= 12 ? template.default_duration : 8);
  const [prompt, setPrompt] = useState(template?.starter_prompt || "");
  const [credits, setCredits] = useState({ video: null, voice: 0 });
  const [busy, setBusy] = useState(false);
  const [savingBrief, setSavingBrief] = useState(false);
  const [savedBriefId, setSavedBriefId] = useState(null);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const tier = effectiveTier(user);
  const isTrial = !tier && user?.role !== "owner";
  const voiceoverAvailable = !isTrial; // Free trial cannot use voiceover

  // Reset state on template change
  useEffect(() => {
    if (!template) return;
    setStep(1);
    setPrompt(template.starter_prompt || "");
    setQuality(template.recommended_quality || "pro");
    setDuration(template.default_duration && template.default_duration <= 12 ? template.default_duration : 8);
    setAsset(null); setMediaSource("generate"); setVoTone(false); setVoScript("");
    setError("");
  }, [template]);

  // Live credit estimate whenever quality/duration/voiceover toggles change
  useEffect(() => {
    if (!template) return;
    let cancelled = false;
    (async () => {
      try {
        const vd = Math.min(12, Math.max(4, Number(duration)));
        const { data: v } = await api.get(`/credits/estimate?feature=video&quality=${quality}&duration=${vd}`);
        let vo = 0;
        if (voTone) {
          const len = Math.max(10, Math.ceil((voScript || prompt || "").length / 16));
          const { data: a } = await api.get(`/credits/estimate?feature=voice&quality=${quality}&duration=${len}`);
          vo = a.credits || 0;
        }
        if (!cancelled) setCredits({ video: v.credits, voice: vo });
      } catch {
        if (!cancelled) setCredits({ video: null, voice: 0 });
      }
    })();
    return () => { cancelled = true; };
  }, [template, quality, duration, voTone, voScript, prompt]);

  if (!template) return null;

  const totalCredits = (credits.video || 0) + (credits.voice || 0);

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true); setError("");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/uploads/asset", form, { headers: { "Content-Type": "multipart/form-data" } });
      setAsset(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
    }
  };

  const goNext = () => {
    setError("");
    if (step === 1 && mediaSource === "upload" && !asset) {
      setError("Upload a photo or video, or switch to 'Generate from prompt'.");
      return;
    }
    if (step === 2 && voTone && !voiceoverAvailable) {
      setError("Voiceover is locked on Free Trial. Upgrade any plan to unlock voice generation.");
      return;
    }
    setStep((s) => Math.min(4, s + 1));
  };
  const goBack = () => setStep((s) => Math.max(1, s - 1));

  const onGenerate = async () => {
    setError(""); setBusy(true);
    try {
      const payload = {
        prompt,
        style: "commercial",
        aspect: template.default_aspect,
        duration: Math.min(12, Math.max(4, Number(duration))),
        quality,
        template_id: template.id,
        asset_id: asset?.asset_id || null,
        add_voiceover: !!voTone,
        voice: voTone ? voice : null,
      };
      const { data } = await api.post("/generate/video", payload);
      sessionStorage.setItem("ybm_last_render", JSON.stringify(data));
      onClose?.();
      navigate(`/generate/video?from_template=${template.id}&render_id=${data.render_id}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const onSaveBrief = async () => {
    setError(""); setSavingBrief(true);
    try {
      const { data } = await api.post("/briefs", {
        title: template.name,
        template_id: template.id,
        prompt,
        aspect: template.default_aspect,
        duration: Math.min(60, Math.max(4, Number(duration))),
        quality,
        add_voiceover: !!voTone,
        voice: voTone ? voice : null,
        voiceover_script: voTone ? voScript || null : null,
        asset_id: asset?.asset_id || null,
      });
      setSavedBriefId(data.brief.brief_id);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingBrief(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-3 py-6 overflow-y-auto"
      data-testid="creator-wizard"
      role="dialog"
      aria-modal="true"
    >
      <div className="relative w-full max-w-5xl bg-[#0E0E0E] border border-[#D4AF37]/30 shadow-[0_20px_80px_rgba(212,175,55,0.15)]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/10 bg-[#0A0A0A]">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.26em] text-[#D4AF37] font-bold mb-1">{template.category}</p>
            <h2 className="font-display text-xl md:text-2xl font-extrabold truncate" data-testid="wizard-template-name">{template.name}</h2>
          </div>
          <button onClick={onClose} data-testid="wizard-close" className="p-2 hover:bg-white/5 text-[#A0A0A0] hover:text-[#F4F4F0]">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Step rail */}
        <div className="px-6 pt-5 pb-3 border-b border-white/10 flex items-center gap-2 overflow-x-auto" data-testid="wizard-steps">
          {STEPS.map((s, i) => {
            const active = s.id === step;
            const done = s.id < step;
            return (
              <React.Fragment key={s.id}>
                <div className={`flex items-center gap-2 px-3 py-1.5 text-[10px] uppercase tracking-wider whitespace-nowrap border ${
                  active ? "border-[#D4AF37] text-[#D4AF37]" : done ? "border-emerald-500/40 text-emerald-300" : "border-[#2A2A2A] text-[#5A5A5A]"
                }`}>
                  <span className="font-mono">{done ? <Check className="w-3 h-3" /> : s.id}</span>
                  <span>{s.label}</span>
                </div>
                {i < STEPS.length - 1 && <div className="h-px w-6 bg-[#2A2A2A]" />}
              </React.Fragment>
            );
          })}
        </div>

        {/* Body */}
        <div className="grid md:grid-cols-5 gap-0">
          {/* Left — visual reference */}
          <div className="md:col-span-2 border-r border-white/10 bg-[#080808]">
            <div className="aspect-[3/4] md:aspect-auto md:h-full bg-cover bg-center"
                 style={{ backgroundImage: `url(${template.thumbnail_url})` }}
                 data-testid="wizard-thumbnail">
              <div className="h-full w-full bg-gradient-to-t from-black/90 via-black/30 to-transparent flex flex-col justify-end p-5">
                <p className="text-[10px] uppercase tracking-wider text-[#D4AF37]/90 mb-1">Scene structure</p>
                <ul className="text-xs text-[#F4F4F0]/90 space-y-1">
                  {(template.scene_structure || []).map((s, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <span className="w-4 h-4 inline-flex items-center justify-center border border-[#D4AF37]/50 text-[10px] text-[#D4AF37]">{i+1}</span>
                      {s}
                    </li>
                  ))}
                </ul>
                <p className="text-[10px] text-[#A0A0A0] mt-3 italic">{template.editing_style}</p>
              </div>
            </div>
          </div>

          {/* Right — step content */}
          <div className="md:col-span-3 p-6 md:p-8 min-h-[440px]">
            {step === 1 && (
              <div data-testid="wizard-step-1">
                <h3 className="font-display text-xl font-bold mb-2">Pick your media source</h3>
                <p className="text-xs text-[#A0A0A0] mb-5">Generate the visual from a prompt — or upload your own photo / short reference video.</p>

                <div className="grid grid-cols-2 gap-3 mb-6">
                  <SourceCard
                    icon={Sparkles} title="Generate from prompt" desc="Start fully from text. Sora 2 will create the scene."
                    active={mediaSource === "generate"} onClick={() => setMediaSource("generate")}
                    testId="source-generate"
                  />
                  <SourceCard
                    icon={Upload} title="Upload your photo / video" desc="Use your own product, face or reference shot."
                    active={mediaSource === "upload"} onClick={() => setMediaSource("upload")}
                    testId="source-upload"
                  />
                </div>

                {mediaSource === "upload" && (
                  <div className="border border-dashed border-[#2A2A2A] hover:border-[#D4AF37]/50 p-5 transition-colors" data-testid="upload-zone">
                    <input
                      ref={fileRef}
                      type="file"
                      accept=".png,.jpg,.jpeg,.webp,.mp4,.mov,.webm,image/*,video/*"
                      onChange={(e) => onUpload(e.target.files?.[0])}
                      className="hidden"
                      data-testid="upload-file-input"
                    />
                    {!asset ? (
                      <div className="text-center">
                        <Upload className="w-7 h-7 text-[#D4AF37] mx-auto mb-3" />
                        <p className="text-sm text-[#F4F4F0] mb-1">Drop a file or click to browse</p>
                        <p className="text-[10px] text-[#5A5A5A] mb-4">Images up to 25 MB · Videos up to 100 MB</p>
                        <button
                          onClick={() => fileRef.current?.click()}
                          disabled={uploading}
                          data-testid="upload-button"
                          className="bg-[#D4AF37] text-[#080808] font-bold px-5 py-2 text-xs disabled:opacity-50"
                        >
                          {uploading ? <><Loader2 className="w-3 h-3 inline animate-spin mr-2" /> Uploading…</> : "Choose file"}
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-4" data-testid="upload-success">
                        {asset.kind === "image" ? (
                          <img src={`${BACKEND_URL}${asset.preview_url}`} alt="" className="w-20 h-20 object-cover border border-[#2A2A2A]" />
                        ) : (
                          <Film className="w-12 h-12 text-[#D4AF37]" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-[#F4F4F0] truncate">{asset.filename}</p>
                          <p className="text-[10px] text-[#A0A0A0]">{(asset.size_bytes/1024/1024).toFixed(2)} MB · {asset.kind}</p>
                        </div>
                        <button
                          onClick={() => { setAsset(null); fileRef.current && (fileRef.current.value = ""); }}
                          className="text-[10px] uppercase tracking-wider text-[#A0A0A0] hover:text-red-300"
                          data-testid="upload-replace"
                        >
                          Replace
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-6">
                  <label className="block text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-2">Prompt</label>
                  <textarea
                    rows={4}
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    className="w-full bg-[#0A0A0A] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-2 text-sm text-[#F4F4F0] resize-y"
                    data-testid="wizard-prompt"
                  />
                  <p className="text-[10px] text-[#5A5A5A] mt-2">Tweak the starter prompt to your brand and product.</p>
                </div>
              </div>
            )}

            {step === 2 && (
              <div data-testid="wizard-step-2">
                <h3 className="font-display text-xl font-bold mb-2">Add a voiceover?</h3>
                <p className="text-xs text-[#A0A0A0] mb-5">An AI voice will narrate over your video.</p>

                {!voiceoverAvailable ? (
                  <div className="border border-amber-500/40 bg-amber-500/10 p-5 mb-6 flex items-start gap-3" data-testid="vo-locked-banner">
                    <Lock className="w-5 h-5 text-amber-300 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm text-amber-100 font-semibold mb-1">Voiceover is a paid feature</p>
                      <p className="text-xs text-amber-100/80 mb-3">
                        Free Trial generates visuals only. Unlock voiceover with any paid plan (Weekly / Starter / Pro / Premium).
                      </p>
                      <button
                        onClick={() => { onClose?.(); navigate("/pricing"); }}
                        className="bg-[#D4AF37] text-[#080808] font-bold px-4 py-2 text-xs"
                        data-testid="vo-upgrade-cta"
                      >
                        See Plans →
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-3 mb-5">
                      <SourceCard
                        icon={MicOff} title="No voiceover" desc="Visual only — silent render."
                        active={!voTone} onClick={() => setVoTone(false)} testId="vo-off"
                      />
                      <SourceCard
                        icon={Mic} title="Add voiceover" desc="Add an AI narrator over the clip."
                        active={voTone} onClick={() => setVoTone(true)} testId="vo-on"
                      />
                    </div>

                    {voTone && (
                      <div className="border border-[#2A2A2A] p-4 bg-[#0A0A0A] space-y-4">
                        <div>
                          <label className="block text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-2">Voice</label>
                          <div className="grid grid-cols-3 gap-2">
                            {VOICES.map((v) => (
                              <button
                                key={v} onClick={() => setVoice(v)} data-testid={`vo-voice-${v}`}
                                className={`px-3 py-2 text-xs border transition-colors ${voice === v ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5" : "border-[#2A2A2A] text-[#A0A0A0]"}`}
                              >
                                {v}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="block text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-2">Voiceover script (optional)</label>
                          <textarea
                            rows={3}
                            value={voScript}
                            onChange={(e) => setVoScript(e.target.value)}
                            placeholder="Leave blank to use the visual prompt as narration"
                            className="w-full bg-[#0A0A0A] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-2 text-sm text-[#F4F4F0]"
                            data-testid="vo-script"
                          />
                        </div>
                        <p className="text-[10px] text-[#5A5A5A]">Voiceover is billed separately from the video — see the credit estimate below.</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {step === 3 && (
              <div data-testid="wizard-step-3">
                <h3 className="font-display text-xl font-bold mb-2">Quality & length</h3>
                <p className="text-xs text-[#A0A0A0] mb-5">Higher quality costs more credits. Cinema uses Sora 2 Pro.</p>

                <p className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-2">Quality</p>
                <div className="grid grid-cols-3 gap-3 mb-5">
                  {QUALITY_OPTIONS.map((q) => (
                    <button
                      key={q.id} onClick={() => setQuality(q.id)} data-testid={`quality-${q.id}`}
                      className={`text-left p-4 border transition-colors ${quality === q.id ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-[#2A2A2A] hover:border-[#D4AF37]/40"}`}
                    >
                      <p className={`font-bold text-sm mb-1 ${quality === q.id ? "text-[#D4AF37]" : "text-[#F4F4F0]"}`}>{q.label}</p>
                      <p className="text-[10px] text-[#A0A0A0]">{q.desc}</p>
                    </button>
                  ))}
                </div>

                <p className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-2">Length</p>
                <div className="grid grid-cols-3 gap-3">
                  {DURATION_OPTIONS.map((d) => (
                    <button
                      key={d.id} onClick={() => setDuration(d.id)} data-testid={`duration-${d.id}`}
                      className={`px-3 py-3 text-xs border transition-colors ${duration === d.id ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5" : "border-[#2A2A2A] text-[#A0A0A0]"}`}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 4 && (
              <div data-testid="wizard-step-4">
                <h3 className="font-display text-xl font-bold mb-4">Review & generate</h3>

                <div className="space-y-2 text-xs mb-5" data-testid="wizard-summary">
                  <Row label="Template" value={template.name} />
                  <Row label="Source" value={mediaSource === "upload" ? (asset ? `Uploaded ${asset.kind}` : "Upload (none)") : "Generated from prompt"} />
                  <Row label="Quality" value={quality.toUpperCase()} />
                  <Row label="Length" value={`${duration}s`} />
                  <Row label="Aspect" value={template.default_aspect} />
                  <Row label="Voiceover" value={voTone ? `Yes · ${voice}` : "No"} />
                </div>

                <div className="border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-4 mb-5" data-testid="credit-estimate">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-[#D4AF37]">Estimated cost</p>
                      <p className="font-display text-3xl font-extrabold text-[#F4F4F0] mt-1">
                        {totalCredits.toLocaleString()} <span className="text-base text-[#D4AF37] font-bold">credits</span>
                      </p>
                    </div>
                    <Coins className="w-8 h-8 text-[#D4AF37]" />
                  </div>
                  <div className="text-[10px] text-[#A0A0A0] mt-3 grid grid-cols-2 gap-2">
                    <span>Video · {(credits.video || 0).toLocaleString()} cr</span>
                    {voTone && <span>Voiceover · {(credits.voice || 0).toLocaleString()} cr</span>}
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="mt-4 px-3 py-2 border border-red-500/40 bg-red-500/10 text-red-200 text-xs flex items-center gap-2" data-testid="wizard-error">
                <AlertTriangle className="w-3.5 h-3.5" /> {error}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-5 border-t border-white/10 bg-[#0A0A0A] flex items-center justify-between gap-3">
          <div className="text-[10px] uppercase tracking-wider text-[#A0A0A0]">
            Step {step} of {STEPS.length} · <span className="text-[#D4AF37]">{totalCredits ? `≈ ${totalCredits} credits` : "—"}</span>
          </div>
          <div className="flex items-center gap-2">
            {step === 4 && (
              <button
                onClick={onSaveBrief}
                disabled={savingBrief || !!savedBriefId}
                data-testid="wizard-save-brief"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-xs border border-[#2A2A2A] hover:border-[#D4AF37] text-[#A0A0A0] hover:text-[#D4AF37] disabled:opacity-50"
                title="Save as a brief — no credits spent. Reload it any time from /briefs."
              >
                {savingBrief ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BookmarkPlus className="w-3.5 h-3.5" />}
                {savedBriefId ? "Saved ✓" : "Save as Brief"}
              </button>
            )}
            {step > 1 && (
              <button onClick={goBack} disabled={busy} className="px-4 py-2 text-xs border border-[#2A2A2A] hover:border-[#D4AF37] text-[#A0A0A0] hover:text-[#F4F4F0]" data-testid="wizard-back">
                Back
              </button>
            )}
            {step < 4 ? (
              <button onClick={goNext} className="inline-flex items-center gap-2 px-5 py-2 text-xs font-bold bg-[#D4AF37] text-[#080808] hover:bg-[#F0C84B]" data-testid="wizard-next">
                Next <ArrowRight className="w-3.5 h-3.5" />
              </button>
            ) : (
              <button
                onClick={onGenerate}
                disabled={busy}
                className="inline-flex items-center gap-2 px-6 py-2 text-xs font-bold bg-[#D4AF37] text-[#080808] hover:bg-[#F0C84B] disabled:opacity-50"
                data-testid="wizard-generate"
              >
                {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending…</> : <><Sparkles className="w-3.5 h-3.5" /> Generate</>}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SourceCard({ icon: Icon, title, desc, active, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`text-left p-4 border transition-colors ${active ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-[#2A2A2A] hover:border-[#D4AF37]/40"}`}
    >
      <Icon className={`w-5 h-5 mb-2 ${active ? "text-[#D4AF37]" : "text-[#A0A0A0]"}`} />
      <p className={`font-bold text-sm mb-1 ${active ? "text-[#D4AF37]" : "text-[#F4F4F0]"}`}>{title}</p>
      <p className="text-[10px] text-[#A0A0A0] leading-relaxed">{desc}</p>
    </button>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between border-b border-[#1A1A1A] pb-2">
      <span className="text-[#A0A0A0] uppercase tracking-wider text-[10px]">{label}</span>
      <span className="text-[#F4F4F0]">{value}</span>
    </div>
  );
}
